import { useMemo, useState, useRef, useEffect } from "react";
import { Search, Plus, MoreVertical, Trash2, Edit3, AlertCircle } from "lucide-react";

export default function SessionLibrary({
  isDarkMode,
  sessionTemplates,
  onRefresh,
  onDoubleClickSession,
  onDeleteSession,
}) {
  const card = isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const menuRef = useRef(null);

  const filtered = useMemo(() => {
    const s = (sessionTemplates || []).slice();
    const qq = q.trim().toLowerCase();
    if (!qq) return s;
    return s.filter((x) => (x.name || "").toLowerCase().includes(qq));
  }, [sessionTemplates, q]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(null);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [menuOpen]);

  const handleDelete = async () => {
    if (deleteConfirm && onDeleteSession) {
      await onDeleteSession(deleteConfirm.id);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className={`rounded-2xl border ${card} p-3`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <div className="relative">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className={`w-full rounded-xl border pl-9 pr-3 py-2 text-sm outline-none ${
                isDarkMode
                  ? "bg-gray-900 border-gray-700 text-gray-100"
                  : "bg-white border-gray-200 text-gray-900"
              }`}
              placeholder="Search for a session..."
            />
          </div>
        </div>

        <button
          onClick={onRefresh}
          className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
            isDarkMode
              ? "bg-gray-900 border-gray-700 hover:bg-gray-800 text-gray-200"
              : "bg-white border-gray-200 hover:bg-gray-50 text-gray-800"
          }`}
          title="Refresh"
        >
          ↻
        </button>
      </div>

      <div className="mt-3 grid gap-2">
        {filtered.map((s) => (
          <div
            key={s.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData("application/x-session-template", JSON.stringify({ id: s.id, name: s.name, total_duration_min: s.total_duration_min || 0 }));
              e.dataTransfer.effectAllowed = "copy";
            }}
            onDoubleClick={() => onDoubleClickSession?.(s.id)}
            className={`group relative rounded-xl border px-3 py-2 cursor-grab active:cursor-grabbing transition ${
              isDarkMode
                ? "bg-gray-900 border-gray-800 hover:bg-gray-800"
                : "bg-white border-gray-200 hover:bg-gray-50"
            }`}
            title="Drag into calendar · Double-click to edit"
          >
            <div className="flex items-center justify-between gap-2">

              {/* Left: name + tags */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{s.name || "Session"}</p>
                {(s.tags || []).length > 0 && (
                  <p className={`truncate text-xs mt-0.5 ${muted}`}>{(s.tags || []).join(" · ")}</p>
                )}
              </div>

              {/* Right: ? tooltip + 3-dot menu — always aligned together on the right */}
              <div className="flex items-center gap-1 flex-shrink-0">

                {/* ? tooltip — only if description exists */}
                {s.description && (
                  <div
                    className="relative group/tip"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold cursor-help border transition
                        ${isDarkMode
                          ? "border-gray-600 text-gray-500 hover:border-blue-400 hover:text-blue-400"
                          : "border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500"
                        }`}
                    >
                      ?
                    </span>

                    {/* Tooltip bubble — anchored to the right to avoid overflow */}
                    <div
                      className={`
                        absolute right-0 bottom-full mb-2 z-50
                        w-60 rounded-xl border px-3 py-2.5 text-xs shadow-xl
                        pointer-events-none
                        opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100
                        transition-all duration-150 origin-bottom-right
                        ${isDarkMode
                          ? "bg-gray-900 border-gray-700 text-gray-200"
                          : "bg-white border-gray-200 text-gray-700"
                        }
                      `}
                    >
                      
                      <p className="leading-relaxed whitespace-pre-wrap">{s.description}</p>
                      {/* Arrow */}
                      <div
                        className={`absolute right-3 top-full w-2 h-2 rotate-45 border-r border-b
                          ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}
                        `}
                      />
                    </div>
                  </div>
                )}

                {/* 3-dot menu */}
                <div className="relative" ref={menuOpen === s.id ? menuRef : null}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(menuOpen === s.id ? null : s.id);
                    }}
                    className={`p-1 rounded-lg opacity-0 group-hover:opacity-100 transition ${
                      isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"
                    } ${menuOpen === s.id ? "opacity-100" : ""}`}
                    title="Options"
                  >
                    <MoreVertical size={14} className={muted} />
                  </button>

                  {/* Dropdown menu */}
                  {menuOpen === s.id && (
                    <div
                      className={`absolute right-0 top-full mt-1 z-50 rounded-lg border shadow-lg py-1 min-w-[140px] ${
                        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(null);
                          onDoubleClickSession?.(s.id);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        }`}
                      >
                        <Edit3 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(null);
                          setDeleteConfirm({ id: s.id, name: s.name });
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-red-500 transition ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        }`}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        ))}
      </div>

      {!filtered.length ? (
        <div className={`mt-3 text-sm ${muted}`}>
          No session.
        </div>
      ) : null}

      <div className="mt-3 text-xs text-emerald-500 flex items-center gap-2">
        <Plus size={14} />
        Tip: create/edit sessions via double-click (or from the calendar).
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl ${
            isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          }`}>
            <div className="p-5">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}>
                  <AlertCircle className="text-red-500" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">Delete session</h3>
                  <p className={`mt-2 text-sm ${muted}`}>
                    Are you sure you want to delete the session <strong>"{deleteConfirm.name}"</strong> from the library?
                  </p>
                  <p className={`mt-2 text-xs ${muted}`}>
                    This action is irreversible. Sessions already placed in the calendar will not be affected.
                  </p>
                </div>
              </div>
            </div>

            <div className={`px-5 py-4 border-t flex justify-end gap-3 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  isDarkMode
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-300"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}